/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { DOCUMENT, Location } from '@angular/common';
import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  HostBinding,
  inject,
  Inject,
  input,
  Input,
  PLATFORM_ID,
  signal,
  ViewEncapsulation
} from '@angular/core';
import { faArrowRight, faCircleNotch, faLocationDot, faMap, faTimes } from '@fortawesome/free-solid-svg-icons';
import { firstValueFrom } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MovieService } from '@usheru-hff/sdk/movie';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import { isTBCDate, isTBCHours } from '@usheru-hff/sdk/utils';
import { getMovieGenres, getMovieTags, PerformanceGroup, ShowtimesFlow } from '@usheru-hff/shared/domain';
import { LogService, ScopedLogger } from '@usheru-hff/shared/utils';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { PanelPerformanceAnimation } from './performance.animations';
import { ShowtimesStoreService } from '../../shared/showtimes.service';

export interface DateItem {
  position: number;
  date?: Date;
  active: boolean;
  hours: HourItem[];
  dayLiteral: string;
  dateLiteral: string;
}

export interface HourItem {
  position: number;
  hourDate?: Date;
  hour: string;
  disabled?: boolean;
  performance: number;
  attributes?: string[];
  redirectionLink?: string;
  showtimeDescription?: string;
  showtimeStatusMessage?: { key: string; label: string };
  tbcActive?: boolean;
}

/**
 * Component that shows book information for a cinema.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 * //TODO barbeitoalex propostal - move component folder to the root of components
 */
@Component({
  selector: 'ush-performance',
  templateUrl: './performance.component.html',
  styleUrls: ['./performance.component.scss'],
  animations: PanelPerformanceAnimation,
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PerformanceComponent implements AfterViewInit {
  readonly channelService = inject(ChannelService);
  private readonly logger: ScopedLogger = inject(LogService).scoped('showtime-performance');

  readonly TBC_TRANSLATE_KEY = 'showtimes.hour.tbc';
  readonly TBC_LONG_TRANSLATE_KEY = 'showtimes.hour.tbc-long';

  @HostBinding('id') id!: string;

  /**
   * @docsNotRequired
   */
  _perfGroup: PerformanceGroup;

  get perfGroup(): PerformanceGroup {
    return this._perfGroup;
  }

  @Input() set perfGroup(perfGroup: PerformanceGroup) {
    this._perfGroup = perfGroup;
    this.id = `cinema-${perfGroup.cinema.id}`;
    this.datesByDay();
  }

  @Input()
  date: Date;

  readonly showtimesFlow = input<ShowtimesFlow>('location');

  readonly isDateFlow = computed(() => this.showtimesFlow() === 'date');

  /**
   *  Basic usage of `ush-performance` won´t include this property.
   *  When used in conjuction with `ush-performance-header` one might one to use `hideAddress`,
   * since there is an address showing already on the **header** associated component.
   * */
  @Input({ transform: booleanAttribute }) hideAddress = false;

  /**
   * If it is active show the {@link ModalMapComponent} with the cinema location.
   */
  showMap = false;
  /**
   * @docsNotRequired
   */
  dates = signal<DateItem[]>([]);
  /**
   * @docsNotRequired
   */
  hours = signal<HourItem[]>([]);

  activatedPlaceholderHours = signal<boolean>(true);

  placeholderHoursList = Array.from({ length: 2 });

  /**
   * @docsNotRequired
   */
  dateSelected: DateItem;
  is12HourFormat = false;
  activeDateAttributes = false;
  /**
   * Status to know if the user is waiting to get the sale status.
   */
  loading = true;

  /**
   * Status to know if the action button is disabled. Sets from the sale status.
   */
  arrowIcon = faArrowRight;
  spinnerIcon = faCircleNotch;
  locationIcon = faLocationDot;
  closeIncon = faTimes;
  mapIcon = faMap;
  movie = this.showtimesStore.movie;
  inlineMap = this.showtimesStore.inlineMap;

  readonly fandangoImg = 'https://cdnstatic.usheru.com/img/others/fandango-logo.png';

  constructor(
    private translationService: TranslationsService,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private movieService: MovieService,
    private showtimesStore: ShowtimesStoreService,
    private cd: ChangeDetectorRef,
    private location: Location,

    @Inject(DOCUMENT) private document: Document,
    @Inject(ENVIRONMENT) private env: Environment,
    @Inject(PLATFORM_ID) private platformId: ObjectConstructor
  ) {
    this.is12HourFormat = this.env.is12HourFormat;
    this.activeDateAttributes = this.env?.showtimeStreamingConfig?.performanceHourAttributes;
  }

  ngAfterViewInit() {
    // Input setter already initializes hours; avoid re-fetching the same showtime ids on first render.
    if (this.perfGroup) {
      this.datesByDay();
    }
    this.cd.detectChanges();
  }

  getShowtimePreparedLink(x: string, hour: HourItem): string | undefined {
    if (!x) return;

    const utmParams = [
      ['utm_source', this.document.location.hostname],
      ['utm_medium', 'usheru'],
      ['utm_campaign', this.movie().friendlyUrl]
    ];

    let url;
    try {
      url = new URL(x);
    } catch (e) {
      console.warn(`Invalid URL at performance ${hour.performance}, ${hour.hourDate.toDateString()}: ${x}`, e);
      return '';
    }

    utmParams.forEach(param => {
      url.searchParams.append(param[0], param[1]);
    });

    // HARDCORED FIX rio-cinema-london
    if (x.indexOf('https://riocinema.org.uk') == 0 || (x.indexOf('glasgowfilm.org') != -1 && x.indexOf('#') > -1)) {
      const separator = x.includes('?') ? '&' : '?';
      const newUrl = `${x}${separator}utm_source=${this.document.location.hostname}&utm_medium=usheru&utm_campaign=${this.movie().friendlyUrl}`;
      return newUrl;
    }
    // HARDCORED FIX for screenaustralia
    if (x.indexOf('https://www.eventcinemas.com.au') == 0) {
      const newUrl = x;
      return newUrl;
    }

    return url.toString();
  }

  /**
   * Add tracking after press the action button.
   */
  goToBuy() {
    this.addTracking();
  }

  /**
   * Add tracking after press the action button.
   *
   * //TODO barbeitoalex propostal - the process with the showtimeLink don't have sense. The a has a href and here does to a reddirection.
   * //TODO barbeito propostal - is the other case is fixed could be merged with {@link goToBuy}.
   */
  goToExternalBuy(hour: HourItem) {
    const showtimeLink = hour?.redirectionLink || '';
    this.debugLog('goToExternalBuy', {
      movie: this.movie()?.title,
      cinema: this.perfGroup?.cinema?.name,
      hour: hour?.hour,
      hasLink: !!showtimeLink
    });
    this.addTracking(false);
    // making sure external link starts with http or https
    if (showtimeLink) {
      if (showtimeLink.indexOf('http://') == 0 || showtimeLink.indexOf('https://') == 0) {
        window.open(showtimeLink, '_blank');
      } else {
        window.open('http://' + showtimeLink, '_blank');
      }
    }
  }

  /**
   * @docsNotRequired
   * @param internal
   */
  private addTracking(internal = true) {
    this.debugLog('Tracking purchase event', {
      action: internal ? 'AddToCart' : 'CinemaRedirect',
      movie: this.movie()?.title,
      cinema: this.perfGroup?.cinema?.name
    });
    this.trackingService.trackEvent({
      action: internal ? 'AddToCart' : 'CinemaRedirect',
      properties: {
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name}`,
        category: 'CinemaPurchase',
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie().imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });
  }

  onFandangoRedirect() {
    this.trackingService.trackEvent({
      action: 'FandangoRedirect',
      properties: {
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name}`,
        category: 'CinemaPurchase',
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie()?.imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });
  }
  /**
   * @docsNotRequired
   */
  openMap() {
    this.showMap = true;
    this.trackingService.trackEvent({
      action: 'OpenMap',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name}`,
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie()?.imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });
    this.cd.detectChanges();
  }

  closeInlineMap() {
    this.showMap = false;
    this.cd.detectChanges();
  }

  // CHECK: New Modal Changes
  /**
   * @docsNotRequired
   * //TODO barbeitoalex propostal - remane to "hideMap"?Pho
   */
  onHideMap() {
    this.showMap = false;
    this.cd.detectChanges();
  }

  /**
   * Sets {@link dates} and the hours for that dates.
   *
   * //TODO barbeitoalex propostal - the name coluld be more descriptive.
   */
  private datesByDay() {
    this.dates.set([]);
    this.hours.set([]);

    const attributes = this.activeDateAttributes ? this.perfGroup?.parsedAttributeSlugs : undefined;
    const perfIds = this.perfGroup.parsedIds;
    const tmpDates = this.perfGroup.parsedLocalTimes;

    this.debugLog('datesByDay:attributes-source', {
      cinema: this.perfGroup?.cinema?.slug ?? this.perfGroup?.cinema?.name ?? null,
      performanceHourAttributesEnabled: this.activeDateAttributes,
      parsedIdsCount: perfIds?.length ?? 0,
      parsedLocalTimesCount: tmpDates?.length ?? 0,
      parsedAttributeGroupsCount: attributes?.length ?? 0,
      parsedAttributesPreview: attributes?.slice(0, 4) ?? [],
      rawAttributeSlugs: this.perfGroup?.attributeSlugs ?? null
    });

    const datesObj: string[] = [];
    const active = false;
    const currentDates: DateItem[] = [];
    tmpDates.forEach((date, i) => {
      const tmp = `${date.getDate()}-${date.getMonth()}`;
      if (!datesObj.includes(tmp)) {
        datesObj.push(tmp);
        currentDates.push({
          date,
          active,
          position: i,
          hours: [
            {
              position: 0,
              hourDate: date,
              hour: this.getHourFormat(date),
              performance: perfIds[i],
              attributes: attributes ? attributes[i] : undefined
            }
          ],
          ...this.getSimplifiedDateFull(date)
        });
      } else {
        currentDates.forEach(o => {
          if (o.date.getDate() === date.getDate() && o.date.getMonth() === date.getMonth()) {
            o.hours.push({
              position: o.hours.length,
              hourDate: date,
              hour: this.getHourFormat(date),
              performance: perfIds[i],
              attributes: attributes ? attributes[i] : undefined
            });
          }
        });
      }
    });

    this.dates.set(currentDates);

    if (this.isDateFlow()) {
      this.dateSelected = this.dates().find(
        d => d.date.getDate() === this.date.getDate() && d.date.getMonth() === this.date.getMonth()
      );
    } else {
      this.dateSelected = this.dates()[0];
    }

    const updatedDates = this.dates().map((d, index) => ({
      ...d,
      active: index === 0
    }));
    this.dates.set(updatedDates);
    this.checkTBCdates();

    this.debugLog('datesByDay:hours-mapped', {
      cinema: this.perfGroup?.cinema?.slug ?? this.perfGroup?.cinema?.name ?? null,
      selectedDate: this.dateSelected?.date?.toISOString?.() ?? null,
      selectedHoursCount: this.dateSelected?.hours?.length ?? 0,
      selectedHoursPreview:
        this.dateSelected?.hours?.slice(0, 6).map(hour => ({
          performance: hour.performance,
          hour: hour.hour,
          attributes: hour.attributes ?? null
        })) ?? []
    });

    Promise.all(this.dateSelected.hours.map(hour => this.processHourStatus(hour))).then(updatedHour => {
      this.hours.set(updatedHour);
      this.activatedPlaceholderHours.set(false);
    });
  }

  getHoursOnDate() {
    const newHours: HourItem[] = [];
    const attributes = this.activeDateAttributes ? this.perfGroup?.parsedAttributeSlugs : [];

    const perfIds = this.perfGroup.parsedIds;
    const tmpDates = this.perfGroup.parsedLocalTimes;
    tmpDates.forEach((date, i) => {
      const tmp = `${date.getDate()}-${date.getMonth()}`;
      const dateStr = `${this.date.getDate()}-${this.date.getMonth()}`;
      if (tmp == dateStr)
        newHours.push({
          position: 0,
          hourDate: date,
          hour: this.getHourFormat(date),
          performance: perfIds[i],
          attributes: attributes ? attributes[i] : undefined
        });
    });

    Promise.all(newHours.map(hour => this.processHourStatus(hour))).then(updatedHours => {
      this.hours.set(updatedHours);
      this.activatedPlaceholderHours.set(false);
    });
  }

  checkTBCdates() {
    // Reference date to know if the status will be TBC
    const tbcReference = new Date(this.dates()[0].date);
    // 12:01AM
    tbcReference.setHours(23, 59);
    //
    const updatedDates = this.dates().map(d => {
      const hoursDate = d.hours;
      const updatedHours = hoursDate.map(h => {
        // if hour is the same as the TBC reference change the hour string
        if (h.hourDate?.getTime() === tbcReference.getTime()) {
          return {
            ...h,
            hour: $localize`:@@showtimes.hour.tbc:TBC`,
            tbcActive: true
          };
        }
        return h;
      });
      return {
        ...d,
        hours: updatedHours
      };
    });
    this.dates.set(updatedDates);
  }

  /**
   * Event to update the available hours after select a day.
   */
  setHours(date: DateItem) {
    this.debugLog('Tracking SelectDate', {
      movie: this.movie()?.title,
      cinema: this.perfGroup?.cinema?.name,
      selectedDate: date?.date?.toISOString?.() || null
    });
    const updatedDates = this.dates().map(d => {
      if (d.position === date.position) {
        this.dateSelected = d;
        Promise.all(d.hours.map(hour => this.processHourStatus(hour))).then(updatedHours => {
          this.hours.set(updatedHours);
          this.activatedPlaceholderHours.set(false);
        });
        return { ...d, active: true };
      } else {
        return { ...d, active: false };
      }
    });
    this.dates.set(updatedDates);

    this.trackingService.trackEvent({
      action: 'SelectDate',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name} @ ${date.date.toLocaleDateString()}`,
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie()?.imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });

    this.cd.detectChanges();
  }

  /**
   * After select an hour runs {@link checkStatus}. Also adds tracking
   * to this event.
   * @param hour
   */
  changeHour(hour: HourItem) {
    this.debugLog('changeHour', {
      movie: this.movie()?.title,
      cinema: this.perfGroup?.cinema?.name,
      hour: hour?.hour,
      disabled: hour?.disabled
    });
    this.hours().forEach(h => {
      if (h.position === hour.position) {
        this.debugLog('selectTime', {
          movie: this.movie()?.title,
          cinema: this.perfGroup?.cinema?.name,
          hour: hour?.hour
        });
      }
    });
    // logic that book now button was doing
    if (!hour.disabled) {
      this.goToExternalBuy(hour);
    }
    this.cd.detectChanges();
  }

  private debugLog(step: string, context?: unknown): void {
    this.logger.log(step, context ?? '');
  }

  processHourStatus(hour: HourItem): Promise<HourItem> {
    return new Promise(resolve => {
      const isTBC = hour.tbcActive;

      if (isTBC) {
        hour.showtimeStatusMessage = {
          key: this.TBC_LONG_TRANSLATE_KEY,
          label: $localize`:@@showtimes.hour.tbc-long:Long`
        };
        hour.disabled = true;
        resolve(hour);
      }

      firstValueFrom(this.movieService.getPerformance(hour.performance))
        .then(performance => {
          if (performance.description) {
            hour.showtimeDescription = performance.description;
          }

          switch (performance.saleStatus) {
            case 10:
              hour.redirectionLink = performance.usheruUrl;
              hour.disabled = false;
              break;
            case 20:
            case 21:
            case 25:
            case 99:
              hour.disabled = false;
              hour.redirectionLink = performance.cinemaUrl || performance.cinema.website || null;
              if (!hour.redirectionLink) {
                hour.showtimeStatusMessage = {
                  key: 'public.showtime.status.soon',
                  label: $localize`:@@public.showtime.status.soon:Coming soon`
                };
                hour.disabled = true;
              }
              break;
            case 50:
              hour.showtimeStatusMessage = {
                key: 'public.showtime.status.sold',
                label: $localize`:@@public.showtime.status.sold:All Tickets Sold`
              };
              break;
            case 52:
              hour.showtimeStatusMessage = {
                key: 'public.showtime.status.close',
                label: $localize`:@@public.showtime.status.close:Online sales closed`
              };
              break;
            case 55:
              hour.showtimeStatusMessage = {
                key: 'public.showtime.status.cancel',
                label: $localize`:@@public.showtime.status.cancel:Cancelled`
              };
              break;
            case 70:
              hour.disabled = true;
              hour.hour = undefined;
              hour.showtimeStatusMessage = {
                key: 'showtimes.hour.tbc',
                label: $localize`:@@showtimes.hour.tbc:TBC`
              };
              break;
          }
          if (!hour.redirectionLink) {
            hour.redirectionLink = this.getShowtimePreparedLink(hour.redirectionLink, hour);
          }

          resolve(hour);
        })
        .catch(err => {
          console.error(err);
          hour.disabled = true;
          resolve(hour);
        });
    });
  }

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - rename to get "get2digitHour"
   * @param date
   * @returns
   */
  getHourFormat(date: Date): string {
    if (isTBCHours(date)) {
      return '  ';
    }
    return date.toLocaleTimeString(this.translationService.currentLanguage().code, {
      hour: 'numeric',
      hour12: this.is12HourFormat,
      minute: '2-digit'
    });
  }

  /**
   * @deprecard Don't found any use
   * @param num
   * @returns
   */
  formatNumber(num: number): string {
    return num <= 9 ? `0${num}` : `${num}`;
  }

  private getSimplifiedDateFull(date: Date): { dayLiteral: string; dateLiteral: string } {
    if (isTBCDate(date)) {
      return {
        dayLiteral: $localize`:@@showtimes.day.tbc:TBC`,
        dateLiteral: ''
      };
    }
    return {
      dayLiteral: this.getSimplifiedDay(date),
      dateLiteral: this.getSimplifiedDate(date)
    };
  }

  /**
   * @docsNotRequired
   *
   * @param date
   * @returns
   */
  private getSimplifiedDate(date: Date): string {
    return date.toLocaleDateString(this.translationService.currentLanguage().code, {
      day: 'numeric',
      month: 'short'
    });
  }

  /**
   * @docsNotRequired
   *
   * @param date
   * @returns
   */
  private getSimplifiedDay(date: Date): string {
    return date.toLocaleDateString(this.translationService.currentLanguage().code, {
      weekday: 'short'
    });
  }

  // CHECK: New Modal Changes
  /**
   * Checks if the {@link perfGroup} has a valid address.
   * @returns
   */
  addressExists(): boolean {
    return !this.perfGroup?.cinema?.place?.address?.includes('null null');
  }

  openMapKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.openMap();
    }
  }
}
